import { dateStringToDate, isOnSameDay } from "./date-utils.js";

export const SLOT_MINUTES = 30;

/**
 * @param {string} time "H:MM" or "HH:MM"
 * @returns minutes since midnight, NaN when invalid
 */
export function timeToMinutes(time) {
  const match = /^(\d{1,2}):(\d{2})$/.exec((time || "").trim());
  if (!match) return NaN;
  return Number(match[1]) * 60 + Number(match[2]);
}

/**
 * @param {number} minutes minutes since midnight
 * @returns "HH:MM"
 */
export function minutesToTime(minutes) {
  const h = String(Math.floor(minutes / 60)).padStart(2, "0");
  const m = String(minutes % 60).padStart(2, "0");
  return `${h}:${m}`;
}

/**
 * Opening hours for a date: an exception for that date wins over the weekday.
 * An open exception without hours falls back to the hours of its weekday.
 *
 * @param {import("../googleScripts/sheet/types").PickupSchedule} schedule
 * @param {Date} date UTC midnight
 * @returns {{from: string, to: string} | null} null when closed
 */
export function getOpeningHours(schedule, date) {
  // sheet order is monday first, getUTCDay is sunday first
  const weekday = schedule.weekdays[(date.getUTCDay() + 6) % 7];

  const exception = schedule.exceptions
    .filter((e) => isOnSameDay(dateStringToDate(e.date), date))
    .pop();

  if (exception) {
    if (!exception.open) return null;
    if (exception.from && exception.to)
      return { from: exception.from, to: exception.to };
    return weekday ? { from: weekday.from, to: weekday.to } : null;
  }

  return weekday?.open ? { from: weekday.from, to: weekday.to } : null;
}

/**
 * Pickup slots for a date, every SLOT_MINUTES from opening until closing,
 * skipping the lunch break.
 *
 * @param {import("../googleScripts/sheet/types").PickupSchedule} schedule
 * @param {Date} date UTC midnight
 * @returns {string[]} ex: ["13:00", "13:30", ...]
 */
export function getSlotsForDay(schedule, date) {
  const hours = getOpeningHours(schedule, date);
  if (!hours) return [];

  const from = timeToMinutes(hours.from);
  const to = timeToMinutes(hours.to);
  if (isNaN(from) || isNaN(to)) return [];

  const lunchFrom = timeToMinutes(schedule.lunch?.from);
  const lunchTo = timeToMinutes(schedule.lunch?.to);

  const slots = [];
  for (let t = from; t < to; t += SLOT_MINUTES) {
    if (t >= lunchFrom && t < lunchTo) continue;
    slots.push(minutesToTime(t));
  }
  return slots;
}

/**
 * @param {import("../googleScripts/sheet/types").PickupSchedule} schedule
 * @param {string} dateString dd/mm/jjjj
 * @param {string} time HH:MM
 * @param {Date} today UTC midnight, only days after today can be picked
 */
export function isAvailableSlot(schedule, dateString, time, today) {
  if (!dateString || !time) return false;
  const date = dateStringToDate(dateString);
  if (isNaN(date.getTime()) || date.getTime() <= today.getTime()) return false;
  return getSlotsForDay(schedule, date).includes(time);
}
